import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ShoppingBag, Plus, Minus, ShoppingCart, CreditCard, Package, Star, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { formatDisplayText } from "@/lib/displayText";
import { formatDateIST } from "@/lib/timeUtils";
import { InvoiceModal, INV_MODAL_CSS, type InvoiceModalTarget } from '@/components/invoices/InvoiceModal';
import { FileText, Loader2 } from "lucide-react";

const PAID_POLL_MS = 10_000;
const PAID_POLL_MAX = 12; // 2 minutes — same cap as the public invoice page
const GOLD = '#c9a962';

interface AddonProduct {
  id: number;
  name: string;
  description: string;
  category: string;
  price: string;
  currency: string;
  isActive: boolean;
  imageUrl?: string;
  features: string[];
  maxQuantity: number;
  isDanceProgram?: boolean;
  approvalStatus?: string;
  approvalStage?: string;
}

interface CartItem {
  product: AddonProduct;
  quantity: number;
}

interface WeddingAddon {
  id: number;
  weddingId: number;
  addonProduct: AddonProduct;
  quantity: number;
  totalPrice: string;
  paymentStatus: 'pending' | 'paid' | 'failed' | 'manual_paid' | 'cancelled';
  addedAt: string;
  notes?: string;
  /**
   * Invoice facts, present only once the line has been invoiced (add-on invoicing §4.3.3).
   * They arrive together — a line either has an invoice or it does not.
   */
  invoiceId?: number | null;
  invoiceNumber?: string | null;
  invoiceBalance?: number | null;
  invoiceStatus?: string | null;
  invoiceToken?: string | null;
  invoiceSkipReason?: string | null;
  danceRequestId?: number | null;
}

// Razorpay checkout.js is NOT in index.html — each page that needs it must load
// it. This page previously assumed window.Razorpay existed, causing "Payment
// system not loaded" when opened directly. Load it on mount AND ensure on click.
function ensureRazorpayLoaded(): Promise<boolean> {
  return new Promise((resolve) => {
    if (typeof window !== "undefined" && (window as any).Razorpay) return resolve(true);
    const SRC = "https://checkout.razorpay.com/v1/checkout.js";
    const existing = document.querySelector(`script[src="${SRC}"]`) as HTMLScriptElement | null;
    if (existing) {
      if ((window as any).Razorpay) return resolve(true);
      existing.addEventListener("load", () => resolve(!!(window as any).Razorpay), { once: true });
      existing.addEventListener("error", () => resolve(false), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = SRC;
    script.async = true;
    script.onload = () => resolve(!!(window as any).Razorpay);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

/**
 * Pay all open add-on invoices with ONE gateway link.
 *
 * 🔴 ADD-ONS ONLY, and that is enforced in SQL rather than here: the group is built from
 * `kind = 'addon'` invoices, so the booking/package invoice can never be swept in. A client
 * paying for a Rs 4,000 add-on is never shown their Rs 2,35,000 venue balance.
 *
 * ⚠️ This REDIRECTS rather than opening a popup, unlike the legacy checkout.js flow. A hosted
 * link is what lets ONE payment settle SEVERAL invoices and be reconciled by the webhook; an
 * in-page handler would have to be told which invoice the money belonged to, and a group has
 * more than one. The page polls on return (`?paid=1`).
 */
function PayAllDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [gateway, setGateway] = useState<string>('');
  const { data: opts, isLoading } = useQuery<{ total: number; gateways: string[]; minPartial: number; blocked: string | null }>({
    queryKey: ['/api/addons/pay/options'],
    queryFn: async () => (await apiRequest('/api/addons/pay/options')).json(),
    enabled: open,
  });

  const start = async () => {
    setBusy(true);
    try {
      const res = await apiRequest('/api/addons/pay', { method: 'POST', data: { gateway, amount: null } });
      const j = await res.json();
      if (!j?.url) throw new Error(j?.error || 'Could not start the payment');
      window.location.href = j.url;
    } catch (e: any) {
      setBusy(false);
      toast({ variant: 'destructive', title: 'Payment could not start', description: e?.message || 'Please try again.' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-gray-900 border-gray-700">
        <DialogHeader>
          <DialogTitle className="text-white">Pay for your add-ons</DialogTitle>
          <DialogDescription className="text-gray-400">
            This covers your add-ons only — your booking invoice is separate and is not included.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center gap-2 text-gray-400 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : opts?.blocked ? (
          <p className="text-amber-300 text-sm py-4">{opts.blocked}</p>
        ) : (
          <div className="space-y-4 py-2">
            <div className="text-2xl font-semibold" style={{ color: GOLD }}>
              Rs {Number(opts?.total ?? 0).toLocaleString('en-IN')}
            </div>
            <div>
              <p className="text-sm text-gray-300 mb-2">Pay using</p>
              <div className="flex gap-2 flex-wrap">
                {(opts?.gateways ?? []).map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setGateway(g)}
                    className={'px-3 py-1.5 rounded border text-sm capitalize ' + (gateway === g
                      ? 'bg-[#c9a962] border-[#c9a962] text-black'
                      : 'bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700')}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-xs text-gray-500">You will be taken to a secure payment page and brought back here.</p>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="border-gray-600 text-gray-200">Close</Button>
          <Button
            onClick={start}
            disabled={busy || isLoading || !!opts?.blocked || !gateway || !(Number(opts?.total) > 0)}
            className="bg-[#c9a962] text-black hover:opacity-90 disabled:opacity-40"
          >
            {busy ? 'Starting…' : 'Pay now'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ClientAddons() {
  const [cart, setCart] = useState<CartItem[]>([]);
  // Preload Razorpay checkout script as soon as the page opens.
  useEffect(() => { void ensureRazorpayLoaded(); }, []);


  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [paymentError, setPaymentError] = useState<string>("");
  const [invoiceTarget, setInvoiceTarget] = useState<InvoiceModalTarget | null>(null);
  const [payAllOpen, setPayAllOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  /**
   * Returned from the hosted payment page (`?paid=1`) — poll until the webhook lands.
   *
   * 🔴 The gateway redirects the client back BEFORE the webhook has necessarily reached us, so
   * the page would otherwise show the add-on still unpaid seconds after they paid, which reads
   * as "my payment failed" and generates the call this page exists to avoid.
   *
   * Bounded: every 10s for at most 2 minutes. An unbounded poll on a page a client may leave
   * open all day is a load problem; if it has not settled in two minutes the webhook needs a
   * human, not more polling.
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('paid') !== '1') return;
    let ticks = 0;
    const t = setInterval(() => {
      ticks += 1;
      void queryClient.refetchQueries({ queryKey: ['/api/addons/wedding'], type: 'active' });
      if (ticks >= PAID_POLL_MAX) clearInterval(t);
    }, PAID_POLL_MS);
    return () => clearInterval(t);
  }, [queryClient]);

  // Fetch available addon products (server already filters for active products only)
  const { data: addonProducts = [], isLoading: productsLoading } = useQuery({
    queryKey: ["/api/addons/products"],
    queryFn: async () => {
      const response = await apiRequest("/api/addons/products");
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - products rarely change
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Fetch client's wedding addons
  const { data: weddingAddons = [], isLoading: addonsLoading } = useQuery({
    queryKey: ["/api/addons/wedding"],
    queryFn: async () => {
      const response = await apiRequest("/api/addons/wedding");
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    },
    staleTime: 30 * 1000, // 30 seconds - cart can change
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
  /**
   * Are ANY add-ons invoiced? Decides which payment path this client gets.
   *
   * 🔴 BOTH PATHS EXIST DURING THE TRANSITION, deliberately. Before cutover no line is
   * invoiced, so "Pay all" would 409 ("no unpaid add-on invoices") and a client could not pay
   * at all if the legacy button were simply deleted. After cutover every line is invoiced and
   * `POST /api/addons/orders` refuses them, so the legacy path goes dark on its own.
   */
  const hasInvoicedAddons = weddingAddons.some((a) => a.invoiceId != null);

  // Fetch user profile for payment prefill
  const { data: user } = useQuery({
    queryKey: ["/api/user/profile"],
    queryFn: async () => {
      const response = await apiRequest("/api/user/profile");
      return response.json();
    },
  });

  // Add to cart mutation
  const addToCartMutation = useMutation({
    mutationFn: async ({ productId, quantity }: { productId: number; quantity: number }) => {
      const response = await apiRequest("/api/addons/cart/add", {
        method: "POST",
        data: { productId, quantity },
      });
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Added to Cart",
        description: "Item has been added to your cart",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/addons/cart"] });
    },
    onError: (error: any) => {
      // Check if this is a dance program requiring approval
      if (error.requiresApproval && error.redirectTo) {
        toast({
          title: "Admin Approval Required",
          description: error.message,
          variant: "destructive",
        });
        // Redirect to dance program booking page
        window.location.href = error.redirectTo;
        return;
      }
      
      toast({
        title: "Error",
        description: error.message || "Failed to add item to cart",
        variant: "destructive",
      });
    },
  });

  // Create order mutation
  const createOrderMutation = useMutation({
    mutationFn: async (cartItems: CartItem[]) => {
      const response = await apiRequest("/api/addons/orders", {
        method: "POST",
        data: { items: cartItems },
      });
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Order Created",
        description: "Opening payment gateway...",
      });
      // Initiate Razorpay payment
      if (data.razorpayOrderId && data.razorpayKeyId) {
        initiateRazorpayPayment(data);
      } else {
        toast({
          title: "Payment Setup Error",
          description: "Unable to initialize payment. Please try again.",
          variant: "destructive",
        });
      }
    },
    onError: (error) => {
      toast({
        title: "Order Creation Failed",
        description: "Please try again or contact support.",
        variant: "destructive",
      });
    },
  });

  const categories = [
    { id: "all", label: "All Services", count: addonProducts.length },
    { id: "decoration", label: "Decoration", count: addonProducts.filter(p => p.category === "decoration").length },
    { id: "catering", label: "Catering", count: addonProducts.filter(p => p.category === "catering").length },
    { id: "entertainment", label: "Entertainment", count: addonProducts.filter(p => p.category === "entertainment").length },
    { id: "photography", label: "Photography", count: addonProducts.filter(p => p.category === "photography").length },
    { id: "transportation", label: "Transportation", count: addonProducts.filter(p => p.category === "transportation").length },
    { id: "special_services", label: "Special Services", count: addonProducts.filter(p => p.category === "special_services").length },
  ];

  const filteredProducts = selectedCategory === "all" 
    ? addonProducts 
    : addonProducts.filter(product => product.category === selectedCategory);

  const addToCart = (product: AddonProduct, quantity: number = 1) => {
    // Check if this is THE Dance Program (approval-required) - redirect without adding to cart.
    // Must NOT match other dance addons like "2 Dance Performance by Desouls" (a normal cart item).
    if (product.name.toLowerCase().includes('dance program')) {
      toast({
        title: "Admin Approval Required",
        description: "Dance programs require admin approval before booking. Redirecting to booking page...",
        variant: "destructive",
      });
      // Redirect to dance program booking page
      setTimeout(() => {
        window.location.href = "/dance-program-booking";
      }, 1500);
      return;
    }
    
    // Add to backend via API for non-dance programs
    addToCartMutation.mutate({ productId: product.id, quantity });
    
    // Also update local cart state for immediate UI feedback
    const existingItem = cart.find(item => item.product.id === product.id);
    if (existingItem) {
      setCart(cart.map(item => 
        item.product.id === product.id 
          ? { ...item, quantity: Math.min(item.quantity + quantity, product.maxQuantity) }
          : item
      ));
    } else {
      setCart([...cart, { product, quantity: Math.min(quantity, product.maxQuantity) }]);
    }
  };

  const removeFromCart = (productId: number) => {
    setCart(cart.filter(item => item.product.id !== productId));
  };

  const updateCartQuantity = (productId: number, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(cart.map(item => 
      item.product.id === productId 
        ? { ...item, quantity: Math.min(quantity, item.product.maxQuantity) }
        : item
    ));
  };

  const getCartTotal = () => {
    const cartTotal = cart.reduce((total, item) => total + (parseFloat(item.product.price) * item.quantity), 0);
    const pendingAddonsTotal = weddingAddons
      .filter(addon => ['pending', 'pending_approval', 'approved'].includes(addon.paymentStatus))
      .reduce((total, addon) => total + parseFloat(addon.totalPrice), 0);
    return cartTotal + pendingAddonsTotal;
  };

  const initiateRazorpayPayment = async (orderData: any) => {
    const rzpReady = await ensureRazorpayLoaded();
    if (!rzpReady || !(window as any).Razorpay) {
      toast({
        title: "Payment Setup Error",
        description: "Payment system not loaded. Please refresh the page.",
        variant: "destructive",
      });
      return;
    }
    const options = {
      key: orderData.razorpayKeyId,
      amount: Math.round(orderData.totalAmount * 100), // Convert to paise
      currency: orderData.currency,
      name: "LA Mirage Koratty",
      description: "Wedding Add-on Services",
      order_id: orderData.razorpayOrderId,
      handler: async function (response: any) {
        try {
          // Verify payment on backend
          const verifyResponse = await apiRequest("/api/addons/verify-payment", {
            method: "POST",
            data: {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            },
          });
          const verifyResult = await verifyResponse.json();

          if (verifyResult.success) {
            toast({
              title: "Payment Successful!",
              description: "Your addon services have been booked successfully.",
            });
            
            // Clear cart and refresh data
            setCart([]);
            queryClient.invalidateQueries({ queryKey: ["/api/addons/wedding"] });
            queryClient.invalidateQueries({ queryKey: ["/api/addons/orders"] });
            // Cart already cleared above via setCart([])
          } else {
            throw new Error("Payment verification failed");
          }
        } catch (error) {
          console.error("Payment verification error:", error);
          toast({
            title: "Payment Verification Failed",
            description: "Please contact support if amount was deducted.",
            variant: "destructive",
          });
        }
      },
      prefill: {
        name: user?.firstName + " " + user?.lastName,
        email: user?.email,
        contact: user?.phone || "",
      },
      theme: {
        color: "#D4AF37", // LA Mirage golden color
      },
    };

    const rzp = new (window as any).Razorpay(options);
    rzp.open();
  };

  const getPaymentStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
      case 'manual_paid':
        return <Badge className="bg-green-500"><Check className="w-3 h-3 mr-1" />Paid</Badge>;
      case 'pending':
        return <Badge variant="outline" className="text-orange-500 border-orange-500">⚠️ Pending Payment</Badge>;
      case 'failed':
        return <Badge variant="destructive">❌ Payment Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (productsLoading || addonsLoading) {
    return (
      <div className="min-h-screen bg-black p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C5A572] mx-auto"></div>
            <p className="text-gray-400 mt-4">Loading add-on services...</p>
          </div>
        </div>
      </div>
    );
  }

  const totalCartItems = cart.length + weddingAddons.filter(addon => ['pending', 'pending_approval', 'approved'].includes(addon.paymentStatus)).length;

  return (
    <div className="min-h-screen bg-black p-3 sm:p-6 mobile-text-optimize overflow-x-hidden">
      <div className="max-w-7xl mx-auto mobile-dashboard-cards">
        {/* Floating Cart Icon - Sticky at top */}
        {totalCartItems > 0 && (
          <div className="fixed top-4 right-4 z-50 animate-pulse">
            <button
              onClick={() => {
                const cartElement = document.getElementById('shopping-cart-section');
                cartElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                cartElement?.classList.add('ring-4', 'ring-[#C5A572]');
                setTimeout(() => {
                  cartElement?.classList.remove('ring-4', 'ring-[#C5A572]');
                }, 2000);
              }}
              className="relative bg-[#C5A572] hover:bg-[#B8975F] text-black p-3 sm:p-4 rounded-full shadow-2xl hover:scale-110 transition-all duration-300"
              title="View Shopping Cart"
            >
              <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-bounce">
                {totalCartItems}
              </span>
            </button>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0 mb-6 sm:mb-8 mobile-touch-spacing">
          <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
            <ShoppingBag className="w-6 h-6 sm:w-8 sm:h-8 text-[#C5A572] flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white break-words">Add-ons & Services</h1>
              <p className="text-sm sm:text-base text-gray-400 break-words">Enhance your {formatDisplayText("wedding")} with premium services</p>
            </div>
          </div>
          
          {totalCartItems > 0 && (
            <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
              <Badge 
                variant="outline" 
                className="text-sm sm:text-lg px-2 sm:px-3 py-1 border-[#C5A572] text-[#C5A572] cursor-pointer hover:bg-[#C5A572]/10 hover:scale-105 transition-all duration-200 shadow-md hover:shadow-lg whitespace-nowrap"
                onClick={() => {
                  const cartElement = document.getElementById('shopping-cart-section');
                  cartElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  cartElement?.classList.add('ring-2', 'ring-[#C5A572]/50');
                  setTimeout(() => {
                    cartElement?.classList.remove('ring-2', 'ring-[#C5A572]/50');
                  }, 2000);
                }}
                title="Click to view cart"
              >
                <ShoppingCart className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden xs:inline">{totalCartItems} items</span>
                <span className="xs:hidden">{totalCartItems}</span>
              </Badge>
              <span className="text-lg sm:text-xl font-bold text-[#C5A572] whitespace-nowrap">
                ₹{getCartTotal().toLocaleString('en-IN')}
              </span>
            </div>
          )}
        </div>

        <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="w-full">
          {/* Mobile: Dropdown Category Selector */}
          <div className="md:hidden mb-6">
            <select 
              className="w-full p-3 bg-gray-800 border border-[#C5A572]/30 rounded-lg text-white text-sm"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.label} {category.count > 0 && `(${category.count})`}
                </option>
              ))}
            </select>
          </div>
          
          {/* Desktop/Tablet: Regular Tab List */}
          <TabsList className="hidden md:grid md:grid-cols-4 lg:grid-cols-7 mb-6 sm:mb-8 bg-gray-900 border-gray-800">
            {categories.map(category => (
              <TabsTrigger 
                key={category.id} 
                value={category.id} 
                className="text-sm data-[state=active]:bg-[#C5A572] data-[state=active]:text-black text-gray-300 hover:text-white"
              >
                <span>{category.label}</span>
                {category.count > 0 && (
                  <Badge variant="secondary" className="ml-2 text-xs bg-gray-700 text-gray-300">
                    {category.count}
                  </Badge>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={selectedCategory} className="space-y-6 sm:space-y-8 overflow-hidden">
            {/* Marketplace */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mobile-grid-optimize">
              {filteredProducts.length === 0 ? (
                <div className="col-span-full text-center py-8 sm:py-12">
                  <Package className="w-12 h-12 sm:w-16 sm:h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-400 mb-2 break-words">No services available in this category</h3>
                  <p className="text-sm sm:text-base text-gray-500 break-words">Check back later for new additions to our marketplace.</p>
                </div>
              ) : (
                filteredProducts.map(product => (
                <Card key={product.id} className="overflow-hidden hover:shadow-xl transition-all bg-gray-900 border-gray-800 hover:border-[#C5A572]/50 mobile-card-optimize w-full">
                  <div className="aspect-w-16 aspect-h-9 bg-gray-800">
                    {product.imageUrl ? (
                      <img 
                        src={product.imageUrl} 
                        alt={product.name}
                        className="w-full h-32 sm:h-48 object-cover"
                      />
                    ) : (
                      <div className="w-full h-32 sm:h-48 bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                        <Package className="w-8 h-8 sm:w-12 sm:h-12 text-[#C5A572]" />
                      </div>
                    )}
                  </div>
                  
                  <CardHeader className="pb-2 p-3 sm:p-4 md:p-6">
                    <div className="flex justify-between items-start gap-2">
                      <CardTitle className="text-sm sm:text-base md:text-lg text-white mobile-title-optimize flex-1 min-w-0 break-words">{product.name}</CardTitle>
                      <Badge variant="outline" className="text-xs border-[#C5A572] text-[#C5A572] mobile-badge-optimize flex-shrink-0 whitespace-nowrap">
                        {product.category.replace('_', ' ')}
                      </Badge>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-3 sm:space-y-4 p-3 sm:p-4 md:p-6 mobile-card-content-optimize">
                    <p className="text-gray-400 text-xs sm:text-sm line-clamp-3 break-words">{product.description}</p>
                    
                    {product.features && product.features.length > 0 && (
                      <div className="space-y-1">
                        {product.features.slice(0, 3).map((feature, index) => (
                          <div key={index} className="flex items-start text-xs text-gray-400 gap-1">
                            <Star className="w-3 h-3 text-[#C5A572] flex-shrink-0 mt-0.5" />
                            <span className="break-words">{feature}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 pt-3 sm:pt-4 border-t border-gray-800">
                      <div className="min-w-0">
                        <span className="text-lg sm:text-xl md:text-2xl font-bold text-[#C5A572] break-words">
                          ₹{parseFloat(product.price).toLocaleString('en-IN')}
                        </span>
                        {product.maxQuantity > 1 && (
                          <p className="text-xs text-gray-500">per unit</p>
                        )}
                      </div>
                      
                      <Button 
                        onClick={() => addToCart(product)}
                        className="bg-[#C5A572] hover:bg-[#B8975F] text-black font-medium mobile-button-optimize text-xs sm:text-sm w-full sm:w-auto"
                        size="sm"
                      >
                        <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        <span className="hidden xs:inline sm:hidden md:inline">Add to Cart</span>
                        <span className="xs:hidden sm:inline md:hidden">Add</span>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Shopping Cart */}
        {(cart.length > 0 || weddingAddons.filter(addon => ['pending', 'pending_approval', 'approved'].includes(addon.paymentStatus)).length > 0) && (
          <Card id="shopping-cart-section" className="mt-6 sm:mt-8 border-2 border-[#C5A572]/50 bg-gray-900 mobile-cart-optimize overflow-hidden">
            <CardHeader className="mobile-touch-spacing p-3 sm:p-6">
              <CardTitle className="flex items-center text-white text-sm sm:text-base">
                <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-[#C5A572] flex-shrink-0" />
                <span className="break-words">Shopping Cart ({cart.length + weddingAddons.filter(addon => ['pending', 'pending_approval', 'approved'].includes(addon.paymentStatus)).length} items)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 sm:space-y-4 mobile-card-optimize p-3 sm:p-6">
              {/* Local cart items */}
              {cart.map(item => (
                <div key={item.product.id} className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-white text-sm sm:text-base break-words">{item.product.name}</h4>
                    <p className="text-xs sm:text-sm text-gray-400 break-words">₹{parseFloat(item.product.price).toLocaleString('en-IN')} each</p>
                  </div>
                  
                  <div className="flex flex-col xs:flex-row items-start xs:items-center gap-2 xs:gap-3 flex-shrink-0">
                    <div className="flex items-center space-x-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="border-gray-600 text-gray-300 hover:bg-gray-700 w-8 h-8 p-0"
                        onClick={() => updateCartQuantity(item.product.id, item.quantity - 1)}
                      >
                        <Minus className="w-3 h-3" />
                      </Button>
                      <span className="w-8 text-center text-white text-sm">{item.quantity}</span>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="border-gray-600 text-gray-300 hover:bg-gray-700 w-8 h-8 p-0"
                        onClick={() => updateCartQuantity(item.product.id, item.quantity + 1)}
                        disabled={item.quantity >= item.product.maxQuantity}
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                    
                    <div className="text-left xs:text-right min-w-0">
                      <p className="font-medium text-[#C5A572] text-sm sm:text-base break-words">₹{(parseFloat(item.product.price) * item.quantity).toLocaleString('en-IN')}</p>
                    </div>
                    
                    <Button 
                      size="sm" 
                      variant="destructive"
                      className="bg-red-600 hover:bg-red-700 text-xs sm:text-sm w-full xs:w-auto"
                      onClick={() => removeFromCart(item.product.id)}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
              
              {/* Pending wedding addons and dance program requests */}
              {/* 2026-09-08: 'schedule_approved' and 'payment_pending' were missing.
                  For a dance program the server maps paymentStatus to the raw
                  request status when unpaid (routes.ts), and
                  dance_program_approval_requests.status runs
                  pending → schedule_approved → payment_pending → approved. So a
                  client whose programme was approved and who now OWES MONEY had
                  the row filtered out of their own add-ons page entirely — they
                  could not see it, let alone pay it. Terminal states
                  (rejected/cancelled) stay excluded from this pending list. */}
              {weddingAddons.filter(addon => ['pending', 'pending_approval', 'schedule_approved', 'payment_pending', 'approved'].includes(addon.paymentStatus)).map((addon, idx, arr) => {
                const isDanceProgram = addon.addonProduct?.isDanceProgram;
                const approvalStatus = addon.addonProduct?.approvalStatus;
                /**
                 * 🔴 An invoiced line's amount sits on a numbered document. Changing the
                 * quantity would change the price of something the client already holds an
                 * invoice for, so the +/- controls disappear. The server refuses it too — a
                 * 403 for the message and a SQL predicate for the guarantee.
                 */
                const isInvoiced = addon.invoiceId != null;
                /**
                 * 🔴 A COMBINED invoice covers SEVERAL lines, and its balance is the whole
                 * document's. Printing it on every line would show one Rs 14,750 invoice three
                 * times — Rs 44,250 of apparent debt for money that is owed once. So the
                 * invoice row renders on the FIRST line carrying that invoice id only.
                 */
                const firstOfInvoice = !isInvoiced || idx === arr.findIndex((x) => x.invoiceId === addon.invoiceId);
                const sharedWith = isInvoiced ? arr.filter((x) => x.invoiceId === addon.invoiceId).length : 1;
                
                return (
                <div key={`addon-${addon.id}`} className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-medium text-white text-sm sm:text-base break-words">{addon.addonProduct.name}</h4>
                      {isDanceProgram && (
                        /* The catch-all used to assert "⏳ Pending Approval" for
                           every other value — including schedule_approved,
                           payment_pending and cancelled. The enum is
                           pending | schedule_approved | payment_pending |
                           approved | rejected | cancelled. */
                        <span className={`text-xs px-2 py-1 rounded ${
                          approvalStatus === 'approved' ? 'bg-green-600/20 text-green-400' :
                          approvalStatus === 'rejected' ? 'bg-red-600/20 text-red-400' :
                          approvalStatus === 'cancelled' ? 'bg-gray-600/20 text-gray-400' :
                          approvalStatus === 'payment_pending' ? 'bg-amber-600/20 text-amber-400' :
                          approvalStatus === 'schedule_approved' ? 'bg-blue-600/20 text-blue-400' :
                          'bg-yellow-600/20 text-yellow-400'
                        }`}>
                          {approvalStatus === 'approved' ? '✓ Approved' :
                           approvalStatus === 'rejected' ? '✗ Rejected' :
                           approvalStatus === 'cancelled' ? 'Cancelled' :
                           approvalStatus === 'payment_pending' ? '⏳ Payment Due' :
                           approvalStatus === 'schedule_approved' ? '✓ Schedule Approved' :
                           '⏳ Pending Approval'}
                        </span>
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-gray-400 break-words">
                      {isDanceProgram ? addon.addonProduct.description : `₹${parseFloat(addon.addonProduct.price).toLocaleString('en-IN')} each`}
                    </p>
                    {/* The invoice, when there is one. The NUMBER is shown as text and not only
                        behind a button — it is what the client quotes on the phone. */}
                    {isInvoiced && firstOfInvoice && (
                      <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-mono" style={{ color: GOLD }}>{addon.invoiceNumber}</span>
                        {sharedWith > 1 && (
                          <span className="text-xs text-gray-400">covers {sharedWith} add-ons</span>
                        )}
                        {addon.invoiceBalance != null && addon.invoiceBalance > 0 ? (
                          <span className="text-xs text-gray-400">₹{addon.invoiceBalance.toLocaleString('en-IN')} due</span>
                        ) : (
                          <span className="text-xs text-green-400">Paid</span>
                        )}
                        {addon.invoiceToken && (
                          <button
                            type="button"
                            onClick={() => setInvoiceTarget({ token: addon.invoiceToken as string, invoiceId: addon.invoiceId as number, weddingId: addon.weddingId, focusPay: false })}
                            className="text-xs underline underline-offset-2 hover:opacity-80"
                            style={{ color: GOLD }}
                          >
                            <FileText className="w-3 h-3 inline mr-1" />
                            View invoice
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-col xs:flex-row items-start xs:items-center gap-2 xs:gap-3 flex-shrink-0">
                    {/* Quantity controls — hidden for dance programmes AND invoiced lines. */}
                    {!isDanceProgram && !isInvoiced && (
                      <div className="flex items-center space-x-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="border-gray-600 text-gray-300 hover:bg-gray-700 w-8 h-8 p-0"
                          onClick={async () => {
                            try {
                              const newQuantity = addon.quantity - 1;
                              if (newQuantity <= 0) {
                                await apiRequest(`/api/addons/cart/${addon.id}`, { method: 'DELETE' });
                                toast({
                                  title: "Item Removed",
                                  description: `${addon.addonProduct.name} removed from cart`,
                                });
                              } else {
                                await apiRequest(`/api/addons/cart/${addon.id}`, {
                                  method: 'PATCH',
                                  data: { quantity: newQuantity }
                                });
                                toast({
                                  title: "Quantity Updated",
                                  description: `${addon.addonProduct.name} quantity updated`,
                                });
                              }
                              await queryClient.refetchQueries({ queryKey: ["/api/addons/wedding"], type: 'active' });
                            } catch (error: any) {
                              toast({
                                title: "Update Failed",
                                description: error.message || "Failed to update quantity",
                                variant: "destructive",
                              });
                            }
                          }}
                        >
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="w-8 text-center text-white text-sm">{addon.quantity}</span>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="border-gray-600 text-gray-300 hover:bg-gray-700 w-8 h-8 p-0"
                          onClick={async () => {
                            try {
                              const newQuantity = addon.quantity + 1;
                              await apiRequest(`/api/addons/cart/${addon.id}`, {
                                method: 'PATCH',
                                data: { quantity: newQuantity }
                              });
                              toast({
                                title: "Quantity Updated",
                                description: `${addon.addonProduct.name} quantity updated`,
                              });
                              await queryClient.refetchQueries({ queryKey: ["/api/addons/wedding"], type: 'active' });
                            } catch (error: any) {
                              toast({
                                title: "Update Failed",
                                description: error.message || "Failed to update quantity",
                                variant: "destructive",
                              });
                            }
                          }}
                          disabled={addon.quantity >= addon.addonProduct.maxQuantity}
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                    
                    <div className="text-left xs:text-right min-w-0">
                      <p className="font-medium text-[#C5A572] text-sm sm:text-base break-words">₹{parseFloat(addon.totalPrice).toLocaleString('en-IN')}</p>
                    </div>
                    
                    {/* Remove/Cancel button */}
                    <Button 
                      size="sm" 
                      variant="destructive"
                      className="bg-red-600 hover:bg-red-700 text-xs sm:text-sm w-full xs:w-auto"
                      onClick={async () => {
                        try {
                          if (isDanceProgram) {
                            // Show confirmation for dance programs
                            const confirmed = window.confirm(
                              `Are you sure you want to cancel this ${approvalStatus === 'approved' ? 'approved ' : ''}dance program request?\n\n` +
                              `${addon.addonProduct.name}\n` +
                              `Amount: ₹${parseFloat(addon.totalPrice).toLocaleString('en-IN')}\n\n` +
                              (approvalStatus === 'approved' ? 'Note: This request has already been approved by admin. Canceling will notify them via WhatsApp.' : 'This action cannot be undone.')
                            );
                            
                            if (!confirmed) return;
                            
                            // Extract dance program request ID from addon.id (format: "dance_14")
                            const danceRequestId = addon.id.toString().replace('dance_', '');
                            
                            const response = await apiRequest(`/api/dance-program-approval/${danceRequestId}/cancel`, {
                              method: 'POST'
                            });
                            
                            const result = await response.json();
                            
                            toast({
                              title: "Request Cancelled",
                              description: result.message || "Dance program request has been cancelled.",
                            });
                            
                            await queryClient.refetchQueries({ queryKey: ["/api/addons/wedding"], type: 'active' });
                            return;
                          }
                          
                          await apiRequest(`/api/addons/cart/${addon.id}`, { method: 'DELETE' });
                          toast({
                            title: "Item Removed",
                            description: `${addon.addonProduct.name} removed from cart`,
                          });
                          await queryClient.refetchQueries({ queryKey: ["/api/addons/wedding"], type: 'active' });
                        } catch (error: any) {
                          toast({
                            title: "Cancellation Failed",
                            description: error.message || "Failed to cancel request",
                            variant: "destructive",
                          });
                        }
                      }}
                    >
                      {isDanceProgram && approvalStatus === 'approved' ? 'Cancel Request' : 'Remove'}
                    </Button>
                  </div>
                </div>
                )
              })}
              
              <div className="border-t border-gray-700 pt-3 sm:pt-4 mobile-payment-optimize">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4">
                  <span className="text-lg sm:text-xl font-bold text-[#C5A572] break-words">Total: ₹{getCartTotal().toLocaleString('en-IN')}</span>
                  {hasInvoicedAddons ? (
                    /* 🔴 INVOICED add-ons are paid through their invoices, as ONE group link.
                       The legacy order flow below refuses them server-side (code INVOICED), so
                       showing it here would only produce an error the client cannot act on. */
                    <Button
                      onClick={() => setPayAllOpen(true)}
                      size="lg"
                      className="bg-[#c9a962] text-black hover:opacity-90"
                    >
                      <CreditCard className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                      <span className="hidden xs:inline">Pay for add-ons</span>
                      <span className="xs:hidden">Pay</span>
                    </Button>
                  ) : (
                  <Button 
                    className="bg-[#C5A572] hover:bg-[#B8975F] text-black font-medium mobile-button-optimize text-sm sm:text-base w-full sm:w-auto"
                    onClick={async () => {
                      try {
                        setPaymentError("");
                        
                        // Clear local cart (items already in backend from "Add to Cart")
                        setCart([]);
                        
                        // Refresh to get latest wedding addons and wait for it
                        await queryClient.refetchQueries({ queryKey: ["/api/addons/wedding"] });
                        
                        // Get fresh data from query cache after refetch
                        const freshWeddingAddons: WeddingAddon[] = queryClient.getQueryData(["/api/addons/wedding"]) || [];

                        console.log('🔍 Payment Click Debug:', {
                          totalAddons: freshWeddingAddons.length,
                          addons: freshWeddingAddons.map(a => ({
                            id: a.id,
                            name: a.addonProduct?.name,
                            isDanceProgram: a.addonProduct?.isDanceProgram,
                            approvalStatus: a.addonProduct?.approvalStatus,
                            paymentStatus: a.paymentStatus
                          }))
                        });

                        // Check for pending dance program approvals
                        const pendingDancePrograms = freshWeddingAddons.filter(addon => 
                          addon.addonProduct?.isDanceProgram && 
                          (addon.addonProduct?.approvalStatus === 'pending' || 
                           addon.addonProduct?.approvalStatus === 'schedule_approved' ||
                           addon.addonProduct?.approvalStatus === 'payment_pending')
                        );

                        console.log('🔍 Pending Dance Programs:', pendingDancePrograms.length, pendingDancePrograms);

                        if (pendingDancePrograms.length > 0) {
                          toast({
                            title: "⏳ Pending Admin Approval",
                            description: "Please wait for admin approval before making payment for dance program services.",
                            variant: "destructive",
                          });
                          return;
                        }

                        // Separate regular addons from dance programs
                        const regularPendingAddons = freshWeddingAddons.filter(addon => 
                          addon.paymentStatus === 'pending' && !addon.addonProduct?.isDanceProgram
                        );
                        const approvedDancePrograms = freshWeddingAddons.filter(addon => 
                          addon.addonProduct?.isDanceProgram && addon.addonProduct?.approvalStatus === 'approved'
                        );
                        
                        const totalAmount = regularPendingAddons.reduce((total, addon) => total + parseFloat(addon.totalPrice), 0);
                        
                        console.log('Payment Debug:', {
                          weddingAddons: weddingAddons.length,
                          regularPendingAddons: regularPendingAddons.length,
                          approvedDancePrograms: approvedDancePrograms.length,
                          totalAmount,
                          pendingDetails: regularPendingAddons.map(addon => ({
                            id: addon.id,
                            name: addon.addonProduct?.name,
                            price: addon.totalPrice,
                            status: addon.paymentStatus
                          }))
                        });

                        console.log('Initiating Razorpay payment gateway...');
                        
                        // Note: Backend now handles both regular addons AND approved dance programs
                        // Just proceed with payment - backend will include everything
                        
                        const combinedTotal = regularPendingAddons.reduce((sum, addon) => sum + parseFloat(addon.totalPrice), 0) +
                                            approvedDancePrograms.reduce((sum, addon) => sum + parseFloat(addon.totalPrice), 0);
                        
                        if (combinedTotal === 0) {
                          toast({
                            title: "No Items to Pay",
                            description: "Please add items to cart first.",
                            variant: "destructive",
                          });
                          return;
                        }

                        const orderResponse = await apiRequest("/api/addons/orders", {
                          method: "POST",
                        });
                        
                        if (!orderResponse.ok) {
                          const errorData = await orderResponse.json();
                          throw new Error(errorData.message || "Failed to create payment order");
                        }
                        
                        const orderData = await orderResponse.json();
                        
                        if (!orderData.orderId) {
                          throw new Error("Invalid payment order received");
                        }

                        const rzpReady = await ensureRazorpayLoaded();
                        if (!rzpReady || !window.Razorpay) {
                          throw new Error("Payment system not loaded. Please refresh the page.");
                        }

                        const options = {
                          key: orderData.keyId,
                          amount: orderData.amount,
                          currency: orderData.currency,
                          name: "LA Mirage Koratty",
                          description: "Wedding Add-on Services",
                          order_id: orderData.orderId,
                          // Review 4.16: the server releases an unpaid order's lines after 30 min;
                          // Checkout closes itself before then so no new attempt starts on it.
                          ...(orderData.timeout ? { timeout: orderData.timeout } : {}),
                          handler: async function (response: any) {
                            try {
                              console.log('Payment successful! Processing payment verification...', response);
                              
                              // Verify payment with the server
                              const verifyResponse = await apiRequest("/api/addons/verify-payment", {
                                method: "POST",
                                data: {
                                  razorpay_payment_id: response.razorpay_payment_id,
                                  razorpay_order_id: response.razorpay_order_id,
                                  razorpay_signature: response.razorpay_signature,
                                },
                              });
                              
                              const verifyData = await verifyResponse.json();
                              
                              if (verifyData.success) {
                                toast({
                                  title: "Payment Successful",
                                  description: "Your add-on services have been confirmed!",
                                });
                                
                                // Force refresh the wedding addons list
                                await queryClient.invalidateQueries({ queryKey: ["/api/addons/wedding"] });
                                await queryClient.refetchQueries({ queryKey: ["/api/addons/wedding"] });
                                setCart([]); // Clear local cart
                              } else {
                                throw new Error("Payment verification failed");
                              }
                              
                            } catch (error) {
                              console.error("Payment verification error:", error);
                              toast({
                                title: "Payment Processing Error",
                                description: "Payment successful but verification failed. Contact support.",
                                variant: "destructive",
                              });
                            }
                          },
                          prefill: {
                            name: user?.firstName + " " + user?.lastName,
                            email: user?.email,
                            contact: user?.phone || "",
                          },
                          theme: {
                            color: "#C5A572",
                          },
                        };

                        const rzp = new (window as any).Razorpay(options);
                        rzp.open();
                        
                      } catch (error) {
                        const errorMessage = error instanceof Error ? error.message : "Payment setup failed. Please try again.";
                        toast({
                          title: "Payment Setup Error",
                          description: errorMessage,
                          variant: "destructive",
                        });
                      }
                    }}
                    disabled={cart.length === 0 && weddingAddons.filter(addon => ['pending', 'pending_approval', 'approved'].includes(addon.paymentStatus)).length === 0}
                    size="lg"
                  >
                    <CreditCard className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                    <span className="hidden xs:inline">Proceed to Payment</span>
                    <span className="xs:hidden">Pay Now</span>
                  </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Purchased Add-ons */}
        {weddingAddons.length > 0 && (
          <Card className="mt-6 sm:mt-8 bg-gray-900 border-gray-800 overflow-hidden">
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="text-white text-sm sm:text-base">Your Wedding Add-ons</CardTitle>
            </CardHeader>
            <CardContent className="p-3 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {weddingAddons.map(addon => (
                  <div key={addon.id} className="p-3 sm:p-4 border border-gray-700 rounded-lg bg-gray-800 overflow-hidden">
                    <div className="flex flex-col xs:flex-row xs:justify-between xs:items-start gap-2 mb-2">
                      <h4 className="font-medium text-white text-sm sm:text-base break-words flex-1 min-w-0">{addon.addonProduct.name}</h4>
                      <div className="flex-shrink-0">{getPaymentStatusBadge(addon.paymentStatus)}</div>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-400 mb-2">Quantity: {addon.quantity}</p>
                    <p className="font-medium text-[#C5A572] text-sm sm:text-base break-words">₹{parseFloat(addon.totalPrice).toLocaleString('en-IN')}</p>
                    {addon.paymentStatus === 'pending' && (
                      <Button size="sm" className="mt-2 w-full xs:w-auto" variant="outline">
                        Pay Now
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* The SHARED portal modal — same component /invoices uses, so the two pages cannot
          disagree about a balance. INV_MODAL_CSS is scoped styling index.css would fight. */}
      <style>{INV_MODAL_CSS}</style>
      <InvoiceModal target={invoiceTarget} onClose={() => setInvoiceTarget(null)} />
      <PayAllDialog open={payAllOpen} onClose={() => setPayAllOpen(false)} />
    </div>
  );
}